import { useGameSearchLite } from './ThumbnailSlider.hook'
import type SwiperCore from 'swiper'
import useIsMobile from '@src/hooks/IsMobile'
import { useCallback, useEffect, useRef, useState } from 'react'
import { CarouselHeader } from '../Elements/CarouselHeader'
import { ThumbnailCarousel } from '../Elements/ThumbnailCarousel'
import { useHistory } from 'react-router-dom'
import { Game } from '../Game/Game.types'
import { GameType } from '@blackjackfun/ui-components'
import { useTranslation } from 'react-i18next'
import { CATEGORY_DETAILS, CategoryKey } from './ThumbnailSlider.helpers'
import CardsIcon from '../icons/cards'
import useIntersectionObserver from '@src/hooks/useIntersectionObserver'
import { handleGameClick } from '@src/components/SearchV2/Search.utils'
import { NormalizedGameOrder } from './__generated__/ThumbnailSliderNormalizedGamesQuery.graphql'
import { useUserRecommendations } from './ThumbnailSlider.recommendations.hook'
import { useSimilarGames } from './ThumbnailSlider.similarGames.hook'
import { isUserLoggedIn } from '@src/utils/functions'

import 'swiper/scss'
import 'swiper/scss/effect-fade'

interface ThumbnailSliderProps {
  category: CategoryKey
  orderBy?: NormalizedGameOrder
  gameCode?: string
  limit?: number
  autoPlay?: boolean
  amount?: number
}

const ThumbnailSlider = ({
  category,
  orderBy = 'POPULAR',
  gameCode,
  autoPlay = true,
  amount = 10,
}: ThumbnailSliderProps) => {
  const { t } = useTranslation()
  const isLoggedIn = isUserLoggedIn()
  const isMobile = useIsMobile()
  const componentRef = useRef(null)
  const swiperRef = useRef<SwiperCore | null>(null)
  const history = useHistory()
  const [swiperInitialized, setSwiperInitialized] = useState(false)

  if (category === 'userrecommendations' && !isLoggedIn) {
    return null
  }

  if (category === 'similar' && !gameCode) {
    return null
  }

  const isVisible = useIntersectionObserver({
    target: componentRef,
    onIntersect: () => {},
    enabled: true,
    rootMargin: '150px',
  })

  // Choose the appropriate hook based on category
  const { games, isLoading } = (() => {
    if (category === 'userrecommendations' && isLoggedIn) {
      return useUserRecommendations(isVisible)
    } else if (category === 'similar' && gameCode) {
      return useSimilarGames(isVisible, gameCode, isMobile)
    } else {
      return useGameSearchLite(isVisible, [], category, orderBy, amount)
    }
  })()

  // Effect to update swiper when games are loaded
  useEffect(() => {
    if (swiperRef.current && swiperInitialized && games.length > 0 && !isLoading) {
      setTimeout(() => {
        if (swiperRef.current) {
          swiperRef.current.update()
        }
      }, 300)
    }
  }, [games, isLoading, swiperInitialized])

  const onGameClick = useCallback(
    (game: Game | GameType) => {
      handleGameClick(game, history)
    },
    [history]
  )

  const handleSwiperInit = (swiper: SwiperCore) => {
    swiperRef.current = swiper
    setSwiperInitialized(true)
  }

  return (
    <div ref={componentRef} className="overflow-x-hidden">
      <CarouselHeader
        text={t(CATEGORY_DETAILS[category]?.textKey || '', CATEGORY_DETAILS[category]?.defaultText || category)}
        swiperRef={swiperRef}
        linkHref={CATEGORY_DETAILS[category]?.linkHref || ''}
        icon={CATEGORY_DETAILS[category]?.icon || <CardsIcon className={'w-16 h-16 text-carouselHeading'} />}
      />

      <ThumbnailCarousel
        games={games as GameType[]}
        isLoading={isLoading}
        isMobile={isMobile}
        onSwiperInit={handleSwiperInit}
        onGameClick={onGameClick}
        autoPlay={autoPlay}
      />
    </div>
  )
}

export default ThumbnailSlider
